<script lang="ts">
    import catDUrl from '$lib/assets/cat-d.png';
    import noPhotoUrl from '$lib/assets/no-photo.svg';
    import {currencies} from '$lib/stores/currencies';
    import {dom_offset, getImageSize, scrollTop} from '$lib/utils-browser';
    import autoAnimate from '@formkit/auto-animate';
    import {createEventDispatcher} from 'svelte';
    import {tooltip} from 'svooltip';

    const dispatch = createEventDispatcher<{remove: {good: GoodItem}}>();

    export let good: GoodItem;
    export let currency: CurrencyItem;
    export let label: string = '';
    export let labelClass: string = '';
    export let removable: boolean = false; // Удаляемый товар

    const price_fmt = new Intl.NumberFormat('ru-RU', {style: 'decimal'});
    const LARGE_PROP_LIMIT = 100;
    let show_all_images = false;
    const show_large_props: {[key: string]: boolean} = {};
    $: category_titles = (good.website_category_title || '').split(' > ');
    $: website_domain = (() => {
        const url = good.url || good.__original?.url;

        if (!url) {
            return '';
        }

        try {
            return new URL(url).hostname;
        } catch (e) {
            return '';
        }
    })();

    $: category_url = good.website_category_url
        ? good.website_category_url.includes('//') || !website_domain
            ? good.website_category_url
            : `http://${website_domain}${good.website_category_url}`
        : '';
    $: prop_list = genPropList(show_large_props, good);
    $: is_http_images = (good.images || []).filter(img => img.startsWith('http://')).length > 0;

    $: currency_show = good.currency !== null;
    $: current_currency = (() => {
        const res = {
            symbol: good.currency,
            title: good.currency,
        };

        if (good.currency === currency.id && currency.symbol) {
            res.symbol = currency.symbol;
        }

        if (good.currency === currency.id && currency.title) {
            res.title = currency.title;
        }

        if (!res.symbol || res.symbol === good.currency || !res.title || res.title === good.currency) {
            const dbCurrency = $currencies.value.find(item => item.id === good.currency);
            if (dbCurrency) {
                res.symbol = dbCurrency.symbol;
                res.title = dbCurrency.title;
            }
        }

        return res;
    })();

    type PropListItem = {
        key: string;
        value: string | string[];
        bold: boolean;
        links: boolean;
        limited: boolean;
        possible_limited: boolean;
    };

    function genPropList(slp: {[key: string]: boolean}, gitem: GoodItem): PropListItem[] {
        const list: PropListItem[] = [];

        if (gitem.article) {
            list.push({
                key: 'Артикул',
                value: gitem.article,
                bold: true,
                links: false,
                limited: false,
                possible_limited: false,
            });
        }

        if (gitem.brand) {
            list.push({
                key: 'Бренд',
                value: gitem.brand,
                bold: true,
                links: false,
                limited: false,
                possible_limited: false,
            });
        }

        if (gitem.quantity) {
            list.push({
                key: 'Остаток',
                value: '' + gitem.quantity,
                bold: true,
                links: false,
                limited: false,
                possible_limited: false,
            });
        }

        if (gitem.short_description) {
            list.push({
                key: 'Краткое описание',
                value: gitem.short_description,
                bold: true,
                links: false,
                limited: false,
                possible_limited: false,
            });
        }

        if (gitem.description) {
            list.push({
                key: 'Описание',
                value: gitem.description,
                bold: true,
                links: false,
                limited: false,
                possible_limited: false,
            });
        }

        if (gitem.data) {
            for (const key in gitem.data) {
                if (!gitem.data.hasOwnProperty(key)) {
                    continue;
                }

                if (key === 'Старая цена') {
                    // Отображается отдельно
                    continue;
                }

                let value = gitem.data[key];

                if (Array.isArray(value)) {
                    if (value.length === 0) {
                        continue;
                    }

                    value = value.map(v => '' + v);

                    list.push({
                        key,
                        value: value,
                        bold: false,
                        links: ['Файлы', 'Видео'].includes(key),
                        limited: false,
                        possible_limited: false,
                    });
                } else {
                    if (value === 0 || value === null || value === undefined) {
                        continue;
                    }

                    if (('' + value).length === 0) {
                        continue;
                    }

                    value = '' + value;

                    list.push({
                        key,
                        value: value,
                        bold: false,
                        links: false,
                        limited: false,
                        possible_limited: false,
                    });
                }
            }
        }

        if (gitem.file_links) {
            list.push({
                key: 'Файлы',
                value: gitem.file_links,
                bold: false,
                links: true,
                limited: false,
                possible_limited: false,
            });
        }

        if (gitem.video_links) {
            list.push({
                key: 'Видео',
                value: gitem.video_links,
                bold: false,
                links: true,
                limited: false,
                possible_limited: false,
            });
        }

        // Сокращенное отображение
        return list.map(item => {
            if (Array.isArray(item.value)) {
                const real_length = item.value.join('').length;
                item.possible_limited = real_length > LARGE_PROP_LIMIT;
                item.limited = !slp[item.key] && item.possible_limited;

                if (item.limited) {
                    let l = 0;
                    let parts = [];
                    for (const v of item.value) {
                        l += v.length;
                        if (l < LARGE_PROP_LIMIT) {
                            parts.push(v);
                        } else {
                            break;
                        }
                    }

                    item.value = parts;
                }
            } else {
                item.possible_limited = item.value.length > LARGE_PROP_LIMIT;
                item.limited = !slp[item.key] && item.possible_limited;

                if (item.limited) {
                    item.value = item.value.substr(0, LARGE_PROP_LIMIT - 3) + '...';
                }
            }

            return item;
        });
    }

    function calcImageStyle(container: HTMLAnchorElement) {
        const img = container.getElementsByClassName('image-large')[0] as HTMLImageElement;
        if (!img) {
            console.warn('calcImageStyle without image-large');
            return;
        }

        container.addEventListener('mouseenter', async () => {
            const imageUrl = container.href.toString();
            img.style.opacity = '0';

            if (container && imageUrl && 'Image' in window) {
                const screenWidth = window.innerWidth || document?.documentElement?.clientWidth;
                const screenHeight = window.innerHeight || document?.documentElement?.clientHeight;

                let size = await getImageSize(imageUrl);
                if (size.width > screenWidth * 0.66) {
                    size.height *= (screenWidth * 0.66) / size.width;
                    size.width = screenWidth * 0.66;
                }

                if (size.height > screenHeight) {
                    size.height = screenHeight - 20;
                }

                let marginTop = 0;
                const offsetTop = dom_offset(container).top - scrollTop();
                const bottomOverflow = screenHeight - (offsetTop + size.height);

                if (bottomOverflow < 0) {
                    marginTop = bottomOverflow - 10;
                }

                img.style.marginTop = marginTop + 'px';
                img.style.opacity = '1';
            }
        });
    }
</script>

<div class="grid p-4 border border-t-0 good-item gap-x-5 gap-y-3 border-gray first:border-t" data-id={good.hash}>
    {#if removable}
        <div class="area-checkbox">
            <button
                type="button"
                title="Удалить товар"
                use:tooltip={{delay: [500, 0]}}
                on:click={() =>
                    dispatch('remove', {
                        good: good,
                    })}
            >
                <svg
                    class="w-4 h-4 cursor-pointer fill-red hover:fill-red-700"
                    viewBox="0 0 438.529 438.529"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M417.689 75.654c-1.711-1.709-3.901-2.568-6.563-2.568h-88.224L302.917 25.41c-2.854-7.044-7.994-13.04-15.413-17.989C280.078 2.473 272.556 0 264.945 0h-91.363c-7.611 0-15.131 2.473-22.554 7.421-7.424 4.949-12.563 10.944-15.419 17.989l-19.985 47.676h-88.22c-2.667 0-4.853.859-6.567 2.568-1.709 1.713-2.568 3.903-2.568 6.567v18.274c0 2.664.855 4.854 2.568 6.564 1.714 1.712 3.904 2.568 6.567 2.568H54.81v271.8c0 15.803 4.473 29.266 13.418 40.398 8.947 11.139 19.701 16.703 32.264 16.703h237.542c12.566 0 23.319-5.756 32.265-17.268 8.945-11.52 13.415-25.174 13.415-40.971V109.627h27.411c2.662 0 4.853-.856 6.563-2.568 1.708-1.709 2.57-3.9 2.57-6.564V82.221c.002-2.664-.861-4.854-2.569-6.567zM169.301 39.678c1.331-1.712 2.95-2.762 4.853-3.14h90.504c1.903.381 3.525 1.43 4.854 3.14l13.709 33.404h-127.91l13.99-33.404zm177.872 340.613c0 4.186-.664 8.042-1.999 11.561-1.334 3.518-2.717 6.088-4.141 7.706-1.431 1.622-2.423 2.427-2.998 2.427H100.493c-.571 0-1.565-.805-2.996-2.427-1.429-1.618-2.81-4.188-4.143-7.706-1.331-3.519-1.997-7.379-1.997-11.561V109.627h255.815v270.664z"
                    />
                    <path
                        d="M137.04 347.172h18.271c2.667 0 4.858-.855 6.567-2.567 1.709-1.718 2.568-3.901 2.568-6.57V173.581c0-2.663-.859-4.853-2.568-6.567-1.714-1.709-3.899-2.565-6.567-2.565H137.04c-2.667 0-4.854.855-6.567 2.565-1.711 1.714-2.568 3.904-2.568 6.567v164.454c0 2.669.854 4.853 2.568 6.57 1.713 1.711 3.9 2.567 6.567 2.567zM210.129 347.172H228.4c2.666 0 4.856-.855 6.564-2.567 1.718-1.718 2.569-3.901 2.569-6.57V173.581c0-2.663-.852-4.853-2.569-6.567-1.708-1.709-3.898-2.565-6.564-2.565h-18.271c-2.664 0-4.854.855-6.567 2.565-1.714 1.714-2.568 3.904-2.568 6.567v164.454c0 2.669.854 4.853 2.568 6.57 1.712 1.711 3.903 2.567 6.567 2.567zM283.22 347.172h18.268c2.669 0 4.859-.855 6.57-2.567 1.711-1.718 2.562-3.901 2.562-6.57V173.581c0-2.663-.852-4.853-2.562-6.567-1.711-1.709-3.901-2.565-6.57-2.565H283.22c-2.67 0-4.853.855-6.571 2.565-1.711 1.714-2.566 3.904-2.566 6.567v164.454c0 2.669.855 4.853 2.566 6.57 1.718 1.711 3.901 2.567 6.571 2.567z"
                    />
                </svg>
            </button>
        </div>
    {/if}

    {#if label}
        <div class="area-checkbox font-bold bg-gray py-3 [writing-mode:vertical-lr] {labelClass}">{label}</div>
    {/if}

    <div class="area-images">
        {#if good.images && good.images.length > 0}
            {#if is_http_images}
                <div class="flex flex-col gap-1">
                    {#each good.images as image, index}
                        {#if show_all_images || index < 5}
                            <div class="overflow-hidden text-sm text-green whitespace-nowrap text-ellipsis">
                                <a
                                    class="link"
                                    class:expanded={show_all_images}
                                    href={image}
                                    target="_blank"
                                    rel="noreferrer noopener nofollow"
                                >
                                    {index + 1})
                                    {image.replace(/^https?:\/\//iu, '')}
                                </a>
                            </div>
                        {/if}
                    {/each}

                    {#if good.images.length > 5}
                        <button
                            type="button"
                            class="p-1 text-xs font-bold text-center border rounded-sm cursor-pointer border-gray"
                            on:click|preventDefault={() => (show_all_images = !show_all_images)}
                        >
                            {#if show_all_images}
                                Свернуть
                            {:else}
                                Еще {good.images.length - 5} фото
                            {/if}
                        </button>
                    {/if}
                </div>
            {:else}
                <div class="flex flex-col gap-1">
                    <div class="flex flex-row flex-wrap gap-1" use:autoAnimate>
                        {#each good.images as image, index}
                            {#if show_all_images || index < 1}
                                <a
                                    class="relative block max-w-[120px] min-h-[30px] mx-auto cursor-zoom-in"
                                    class:expanded={show_all_images}
                                    href={image}
                                    use:calcImageStyle
                                    target="_blank"
                                    rel="noreferrer noopener nofollow"
                                >
                                    <img alt="Фото {index + 1}" loading="lazy" class="min-w-full max-h-[120px] peer" src={image} />
                                    <img
                                        alt="Фото {index + 1}"
                                        style="max-width:66vw; max-height:calc(100vh - 20px);"
                                        class="image-large hidden peer-hover:block absolute top-0 left-full ml-1 pointer-events-none z-[10000] bg-white shadow-lg"
                                        src={image}
                                    />
                                </a>
                            {/if}
                        {/each}
                    </div>

                    {#if good.images.length > 1}
                        <button
                            type="button"
                            class="p-1 text-xs font-bold text-center border rounded-sm cursor-pointer border-gray"
                            on:click|preventDefault={() => (show_all_images = !show_all_images)}
                        >
                            {#if show_all_images}
                                Свернуть
                            {:else}
                                Еще {good.images.length - 1} фото
                            {/if}
                        </button>
                    {/if}
                </div>
            {/if}
        {:else}
            <div class="max-w-[120px] mx-auto">
                <img class="max-w-full" alt="Нет фото" src={noPhotoUrl} />
            </div>
        {/if}
    </div>

    <div class="area-title">
        <a href={good.url} target="_blank" rel="noreferrer noopener nofollow" class="text-lg text-black md:text-xl font-brand-bold">
            {good.title || '[Без названия]'}
        </a>

        {#if good.url}
            <a
                href={good.url}
                target="_blank"
                rel="noreferrer noopener nofollow"
                class="text-xs break-all text-black-300 line-clamp-1 text-ellipsis"
            >
                {good.url}
            </a>
        {/if}

        <div class="flex flex-wrap items-center content-start mt-3 gap-x-3">
            {#each category_titles as title, index}
                {#if index > 0}
                    <img src={catDUrl} alt=">" width="4" height="5" />
                {/if}
                <a href={category_url} target="_blank" rel="noreferrer noopener nofollow" class="text-sm text-black-500">
                    {title}
                </a>
            {/each}
            <!-- Фикс анимации переключения товаров -->
            <span class="invisible">.</span>
        </div>
    </div>

    {#if typeof good.price === 'number' || typeof good.old_price === 'number' || typeof good.publish === 'boolean'}
        <div class="flex items-center area-status gap-x-6 gap-y-3">
            {#if typeof good.publish === 'boolean'}
                <div>
                    <div
                        class="text-sm px-2 py-1 border rounded-sm {good.publish
                            ? 'bg-green-50 text-green border-green'
                            : 'bg-black-50 text-black-500 border-gray'}"
                    >
                        {good.publish ? 'В наличии' : 'Нет в наличии'}
                    </div>
                </div>
            {/if}

            {#if typeof good.price === 'number'}
                <div class="text-lg md:text-xl font-brand-bold">
                    <span title={good.__original ? `Оригинальная цена ${good.__original.price} ${good.__original.currency}` : ''}>
                        {price_fmt.format(good.price)}
                    </span>{#if currency_show}&nbsp;<span
                            use:tooltip={{content: `${current_currency.title} (${good.currency})`, delay: [500, 0]}}
                            >{current_currency.symbol}</span
                        >{/if}
                </div>
            {/if}

            {#if typeof good.old_price === 'number'}
                <div class="text-lg price-old md:text-xl font-brand text-black-500">
                    <span>
                        {price_fmt.format(good.old_price)}
                    </span>{#if currency_show}&nbsp;<span
                            use:tooltip={{content: `${current_currency.title} (${good.currency})`, delay: [500, 0]}}
                            >{current_currency.symbol}</span
                        >{/if}
                </div>
            {/if}
        </div>
    {/if}

    {#if prop_list.length > 0}
        <div class="flex flex-col gap-2 overflow-hidden text-sm leading-5 area-properties">
            {#each prop_list as prop}
                <div class="flex gap-3 hover:ring-4 hover:ring-gray-50 hover:bg-gray-50">
                    <div class="text-black-500 min-w-[80px] w-2/12 max-w-sm overflow-hidden shrink-0" class:font-bold={prop.bold}>
                        {prop.key}:
                    </div>
                    <div class="overflow-hidden break-words whitespace-pre-line">
                        {#if Array.isArray(prop.value)}
                            {#each prop.value as value}
                                {#if prop.links}
                                    <div>
                                        <a
                                            class="underline break-all underline-offset-2 decoration-dotted"
                                            href={value}
                                            target="_blank"
                                            rel="noreferrer noopener nofollow">{value}</a
                                        >
                                    </div>
                                {:else}
                                    <span class="mr-2 underline underline-offset-2 decoration-dotted">{value}</span>
                                {/if}
                            {/each}
                        {:else}
                            {prop.value}
                        {/if}
                        {#if prop.limited}
                            <button type="button" class="link ajax" on:click={() => (show_large_props[prop.key] = true)}>Показать</button>
                        {:else if prop.possible_limited}
                            <br />
                            <button type="button" class="link ajax" on:click={() => (show_large_props[prop.key] = false)}>Скрыть</button>
                        {/if}
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>

<style lang="postcss">
    .good-item {
        grid-template-columns: 25px 1fr;
        grid-template-areas:
            'checkbox image'
            'title title'
            'status status'
            'propeties propeties';

        @media (min-width: 640px) {
            grid-template-columns: 25px 120px 1fr;
            grid-template-rows: min-content min-content 1fr;
            grid-template-areas:
                'checkbox image title'
                'checkbox image status'
                'checkbox image propeties';
        }

        .area-checkbox {
            grid-area: checkbox;
        }

        .area-images {
            grid-area: image;
            padding-right: 25px;

            @media (min-width: 640px) {
                padding-right: 0;
            }
        }

        .area-title {
            grid-area: title;
        }

        .area-properties {
            grid-area: propeties;
        }

        .area-status {
            grid-area: status;
        }
    }

    .price-old {
        position: relative;

        &:after {
            content: '';
            position: absolute;
            opacity: 0.9;
            left: -5px;
            right: -5px;
            top: 45%;
            display: block;
            height: 3px;
            background-color: currentColor;
            transform: rotate(-10deg);
            pointer-events: none;
        }
    }
</style>
